import { GraphQLError } from 'graphql'

export type ErrorCategory =
  | 'network'
  | 'permission'
  | 'notFound'
  | 'validation'
  | 'fallback'

interface ApolloLikeError {
  networkError?: unknown
  graphQLErrors?: Array<{
    extensions?: {
      code?: string
      originalError?: { code?: string; statusCode?: number }
    }
  }>
}

/**
 * 에러 객체를 사용자 표시용 카테고리로 분류.
 * - 인증 관련(UNAUTHENTICATED, TOKEN_EXPIRED, INVALID_TOKEN)은 여기에 도달하기 전
 *   authErrorLink/refreshTokenLink가 이미 처리하므로 고려하지 않는다.
 * - `error.message`를 그대로 노출하지 않기 위해 카테고리별 i18n 키만 반환.
 */
export function categorize(error: unknown): ErrorCategory {
  if (!error) return 'fallback'

  const apolloLike = error as ApolloLikeError & { message?: string }
  if (apolloLike.networkError) return 'network'

  const codes = (apolloLike.graphQLErrors ?? [])
    .map((e) => e.extensions?.code ?? e.extensions?.originalError?.code)
    .filter(Boolean) as string[]

  if (codes.includes('FORBIDDEN')) return 'permission'
  if (codes.includes('NOT_FOUND')) return 'notFound'
  if (codes.includes('BAD_USER_INPUT')) return 'validation'

  // graphql-js `GraphQLError` 직접 생성된 경우
  if (error instanceof GraphQLError) {
    const code = error.extensions?.code as string | undefined
    if (code === 'FORBIDDEN') return 'permission'
    if (code === 'NOT_FOUND') return 'notFound'
    if (code === 'BAD_USER_INPUT') return 'validation'
  }

  return 'fallback'
}

export const CATEGORY_MESSAGE_KEY: Record<ErrorCategory, string> = {
  network: 'common:errorPage.category.network',
  permission: 'common:errorPage.category.permission',
  notFound: 'common:errorPage.category.notFound',
  validation: 'common:errorPage.category.validation',
  fallback: 'common:errorPage.category.fallback',
}

export type CategoryAction = 'retry' | 'home' | 'none'

export const CATEGORY_ACTION: Record<ErrorCategory, CategoryAction> = {
  network: 'retry',
  permission: 'none',
  notFound: 'home',
  validation: 'retry',
  fallback: 'retry',
}
